export type CompletePercentageFunc = (percentage: number) => void;

export async function pollResults<R extends {data: {status: string; progressPercent?: number}}>(params: {
  importId: string;
  maxWaitSec: number;
  completePercentage?: CompletePercentageFunc;
  fetcher: () => Promise<R>;
}): Promise<R['data']> {
  const {importId, maxWaitSec, completePercentage, fetcher} = params;
  const pollIntervalMs = window.Cypress ? 5000 : 2000;
  const startTime = Date.now();
  const maxWaitMs = maxWaitSec * 1000;

  try {
    let resp;
    try {
      resp = await fetcher();
    } catch (error) {
      throw error;
    }

    let data = resp.data;

    if (typeof completePercentage === 'function') {
      completePercentage(data.progressPercent);
    }

    // Poll until completion, failure, or timeout
    while (data && (data.status === 'queued' || data.status === 'running') && Date.now() - startTime < maxWaitMs) {
      await sleep(pollIntervalMs);

      try {
        const response = await fetcher();
        data = response.data;

        if (typeof completePercentage === 'function') {
          completePercentage(data.progressPercent);
        }
      } catch (error) {
        // Continue polling despite error
      }
    }

    const elapsedSec = Math.round((Date.now() - startTime) / 1000);

    // Check why we exited the loop
    if (Date.now() - startTime >= maxWaitMs) {
      console.warn(
        `[pollResults] Polling timed out after ${elapsedSec}s for import ${importId}. Final status: ${
          data?.status
        }, Progress: ${data?.progressPercent || 0}%`,
      );
      throw new Error(`Polling timed out after ${maxWaitSec} seconds for import ${importId}`);
    }

    if (!data) {
      console.error(`[pollResults] No data received for import ${importId} after ${elapsedSec}s`);
      throw new Error(`Failed to retrieve data for import ${importId}`);
    }

    return data;
  } catch (error) {
    const elapsedSec = Math.round((Date.now() - startTime) / 1000);
    console.error(`[pollResults] Error after ${elapsedSec}s for import ${importId}:`, error);

    // Enhance the error
    const enhancedError = error instanceof Error ? error : new Error(String(error));
    enhancedError.message = `Polling failed for import ${importId}: ${enhancedError.message}`;

    throw enhancedError;
  }
}

export function runAllSettled<T>(promises: Promise<T>[]) {
  return Promise.allSettled(promises).then((res) => {
    return res.reduce(
      (all, cur) => {
        if (cur.status === 'fulfilled') all.fulfilled.push(cur);
        else all.rejected.push(cur);
        return all;
      },
      {fulfilled: [] as PromiseFulfilledResult<T>[], rejected: [] as PromiseRejectedResult[]},
    );
  });
}

async function sleep(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}
